/**
 * Calculates the distance between two geographic coordinates using the Haversine formula.
 * 
 * @param lat1 Latitude of first point in decimal degrees
 * @param lon1 Longitude of first point in decimal degrees
 * @param lat2 Latitude of second point in decimal degrees
 * @param lon2 Longitude of second point in decimal degrees
 * @param unit 'K' = kilometers, 'M' = miles (default), 'N' = nautical miles
 * @returns Distance in specified unit
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  unit: 'K' | 'M' | 'N' = 'M'
): number => {
  const toRadians = (deg: number): number => deg * (Math.PI / 180);

  const radiusByUnit: Record<'K' | 'M' | 'N', number> = {
    K: 6371.0088,    // Kilometers
    M: 3958.7613,    // Miles
    N: 3440.0695     // Nautical Miles
  };
  // console.log('Calculating distance:', lat1, lon1, lat2, lon2, unit);
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);

  const a = Math.sin(Δφ / 2) ** 2 +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const radius = radiusByUnit[unit];

  return radius * c;
};
